import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface Partner {
  id: string;
  name: string;
  slug: string;
  status: string;
  billingEmail: string | null;
  createdAt: string;
  memberCount: number;
  clientCount: number;
}

interface PartnerDetail extends Partner {
  members: { userId: string; role: string; userName: string; userEmail: string; joinedAt: string }[];
  clients: { linkId: string; tenantId: string; tenantName: string; deliveryModel: string; linkedAt: string }[];
}

type NotifChannel = 'EMAIL' | 'WEB_PUSH' | 'WHATSAPP' | 'IN_APP';

interface NotifConfig {
  configured: boolean;
  channels: NotifChannel[];
  supportedChannels: NotifChannel[];
  updatedAt: string | null;
  updatedBy: string | null;
}

const NOTIF_CHANNEL_META: Record<Exclude<NotifChannel, 'IN_APP'>, {
  label: string;
  disabled: boolean;
  tooltip?: string;
}> = {
  EMAIL:    { label: 'Email', disabled: false },
  WEB_PUSH: { label: 'Web Push', disabled: false },
  WHATSAPP: { label: 'WhatsApp', disabled: true, tooltip: 'WhatsApp requires Business Account registration' },
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#16a34a',
  PENDING: '#d97706',
  SUSPENDED: '#dc2626',
};

export default function PlatformAdminPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerDetail | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createForm, setCreateForm] = useState({ name: '', slug: '', billingEmail: '' });
  // NOTIF.C1 — per-client notification config expansion state
  const [expandedNotif, setExpandedNotif] = useState<string | null>(null);
  const expandedNotifRef = useRef<string | null>(null);
  const [notifConfig, setNotifConfig] = useState<NotifConfig | null>(null);
  const [notifSelected, setNotifSelected] = useState<Set<NotifChannel>>(new Set());
  const [notifInitial, setNotifInitial] = useState<Set<NotifChannel>>(new Set());
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState('');
  const [notifSaved, setNotifSaved] = useState(false);

  const token = localStorage.getItem('blueprint_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchPartners = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/partners`, { headers });
      if (res.status === 403) { navigate('/dashboard'); return; }
      if (!res.ok) throw new Error('Failed to load partners');
      setPartners(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    try {
      const payload = Object.fromEntries(
        Object.entries(createForm).filter(([, v]) => v !== '')
      );
      const res = await fetch(`${API}/api/admin/partners`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json(); setError(d.error); return; }
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', billingEmail: '' });
      fetchPartners();
    } catch (e: any) { setError(e.message); }
  };

  const viewDetail = async (id: string) => {
    resetNotifState();
    const res = await fetch(`${API}/api/admin/partners/${id}`, { headers });
    if (res.ok) setSelectedPartner(await res.json());
  };

  const toggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    const res = await fetch(`${API}/api/admin/partners/${id}`, {
      method: 'PATCH', headers, body: JSON.stringify({ status: newStatus }),
    });
    if (res.ok) {
      fetchPartners();
      if (selectedPartner?.id === id) viewDetail(id);
    }
  };

  const resetNotifState = () => {
    expandedNotifRef.current = null;
    setExpandedNotif(null);
    setNotifConfig(null);
    setNotifSelected(new Set());
    setNotifInitial(new Set());
    setNotifLoading(false);
    setNotifSaving(false);
    setNotifError('');
    setNotifSaved(false);
  };

  const loadNotifConfig = async (partnerId: string, clientTenantId: string) => {
    setNotifConfig(null);
    setNotifSelected(new Set());
    setNotifInitial(new Set());
    setNotifLoading(true);
    setNotifError('');
    setNotifSaved(false);
    try {
      const res = await fetch(
        `${API}/api/admin/partners/${partnerId}/clients/${clientTenantId}/notifications`,
        { headers },
      );
      if (expandedNotifRef.current !== clientTenantId) return;
      if (!res.ok) throw new Error('Failed to load notification config');
      const cfg: NotifConfig = await res.json();
      setNotifConfig(cfg);
      const channels = new Set<NotifChannel>(cfg.channels);
      setNotifSelected(channels);
      setNotifInitial(new Set(channels));
    } catch (e: any) {
      if (expandedNotifRef.current === clientTenantId) setNotifError(e.message);
    } finally {
      setNotifLoading(false);
    }
  };

  const saveNotifConfig = async (partnerId: string, clientTenantId: string) => {
    setNotifSaving(true);
    setNotifError('');
    setNotifSaved(false);
    try {
      const res = await fetch(
        `${API}/api/admin/partners/${partnerId}/clients/${clientTenantId}/notifications`,
        { method: 'PATCH', headers, body: JSON.stringify({ channels: Array.from(notifSelected) }) },
      );
      if (expandedNotifRef.current !== clientTenantId) return;
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Save failed'); }
      const cfg: NotifConfig = await res.json();
      setNotifConfig(cfg);
      const channels = new Set<NotifChannel>(cfg.channels);
      setNotifSelected(channels);
      setNotifInitial(new Set(channels));
      setNotifSaved(true);
    } catch (e: any) {
      if (expandedNotifRef.current === clientTenantId) setNotifError(e.message);
    } finally {
      setNotifSaving(false);
    }
  };

  const toggleNotifChannel = (ch: NotifChannel) => {
    setNotifSaved(false);
    setNotifSelected(prev => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  const handleExpandNotif = (clientTenantId: string) => {
    if (expandedNotif === clientTenantId) {
      resetNotifState();
      return;
    }
    resetNotifState();
    expandedNotifRef.current = clientTenantId;
    setExpandedNotif(clientTenantId);
    if (selectedPartner) loadNotifConfig(selectedPartner.id, clientTenantId);
  };

  const notifDirty = (() => {
    if (!notifConfig) return false;
    if (notifSelected.size !== notifInitial.size) return true;
    for (const ch of notifSelected) if (!notifInitial.has(ch)) return true;
    return false;
  })();

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--surface-alt)',
    color: 'var(--text)', fontSize: '14px',
  };

  const btnPrimary: React.CSSProperties = {
    padding: '8px 16px', borderRadius: '6px', border: 'none',
    background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 600,
  };

  const btnGhost: React.CSSProperties = {
    padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: '13px',
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text)' }}>Loading...</div>;

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto', color: 'var(--text)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Platform Admin</h1>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ Create Partner Account</button>
      </div>

      {error && <div style={{ padding: '8px 16px', background: '#fef2f2', color: '#dc2626', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 24, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Create Partner Account</h2>
            {(['name', 'slug', 'billingEmail'] as const).map(field => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-muted)' }}>
                  {field === 'billingEmail' ? 'Billing Email' :
                   field.charAt(0).toUpperCase() + field.slice(1)}
                  {field === 'slug' && <span style={{ fontSize: 11 }}> (auto-generated if empty)</span>}
                </label>
                <input
                  style={inputStyle}
                  value={createForm[field]}
                  onChange={e => setCreateForm(p => ({ ...p, [field]: e.target.value }))}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnGhost} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={btnPrimary} onClick={handleCreate}>Create Partner Account</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Partner list table */}
        <div style={{ flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                {['Name', 'Status', 'Clients', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>{p.name}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: `${STATUS_COLORS[p.status] ?? '#888'}20`, color: STATUS_COLORS[p.status] ?? '#888',
                    }}>{p.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>{p.clientCount}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                    <button style={btnGhost} onClick={() => viewDetail(p.id)}>View</button>
                    <button style={btnGhost} onClick={() => toggleStatus(p.id, p.status)}>
                      {p.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {partners.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No partner accounts yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {selectedPartner && (
          <div style={{ width: 360, background: 'var(--surface-alt)', borderRadius: 12, padding: 20, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{selectedPartner.name}</h3>
              <button style={{ ...btnGhost, fontSize: 12 }} onClick={() => { resetNotifState(); setSelectedPartner(null); }}>Close</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              <div>Slug: {selectedPartner.slug}</div>
              <div>Billing: {selectedPartner.billingEmail ?? '—'}</div>
              <div>Status: {selectedPartner.status}</div>
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Members ({selectedPartner.members.length})</h4>
            {selectedPartner.members.map(m => (
              <div key={m.userId} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <div style={{ fontWeight: 500 }}>{m.userName}</div>
                <div style={{ color: 'var(--text-muted)' }}>{m.userEmail} · {m.role}</div>
              </div>
            ))}

            <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Clients ({selectedPartner.clients.length})</h4>
            {selectedPartner.clients.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No clients linked</div>}
            {selectedPartner.clients.map(c => (
              <div key={c.linkId} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{c.tenantName}</div>
                    <div style={{ color: 'var(--text-muted)' }}>{c.deliveryModel} · {new Date(c.linkedAt).toLocaleDateString()}</div>
                  </div>
                  <button
                    style={{ ...btnGhost, fontSize: 11, padding: '3px 8px' }}
                    onClick={() => handleExpandNotif(c.tenantId)}
                  >
                    {expandedNotif === c.tenantId ? 'Hide' : 'Notifications'}
                  </button>
                </div>

                {expandedNotif === c.tenantId && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg)', borderRadius: 8 }}>
                    {notifLoading && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading...</div>}
                    {notifError && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 6 }}>{notifError}</div>}

                    {notifConfig && !notifLoading && (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>
                          Notification Channels
                        </div>
                        {(Object.entries(NOTIF_CHANNEL_META) as [Exclude<NotifChannel, 'IN_APP'>, typeof NOTIF_CHANNEL_META[keyof typeof NOTIF_CHANNEL_META]][]).map(([ch, meta]) => (
                          <label
                            key={ch}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                              cursor: meta.disabled ? 'not-allowed' : 'pointer',
                              opacity: meta.disabled ? 0.5 : 1,
                            }}
                            title={meta.tooltip}
                          >
                            <input
                              type="checkbox"
                              checked={notifSelected.has(ch)}
                              disabled={meta.disabled || notifSaving}
                              onChange={() => toggleNotifChannel(ch)}
                              style={{ accentColor: 'var(--accent)' }}
                            />
                            <span style={{ fontSize: 13 }}>{meta.label}</span>
                            {meta.tooltip && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>(?)</span>}
                          </label>
                        ))}

                        {notifConfig.updatedAt && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                            Last updated: {new Date(notifConfig.updatedAt).toLocaleString()}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                          <button
                            style={{
                              ...btnPrimary, fontSize: 12, padding: '4px 12px',
                              opacity: (notifDirty && !notifSaving) ? 1 : 0.5,
                              cursor: (notifDirty && !notifSaving) ? 'pointer' : 'default',
                            }}
                            disabled={!notifDirty || notifSaving}
                            onClick={() => saveNotifConfig(selectedPartner!.id, c.tenantId)}
                          >
                            {notifSaving ? 'Saving...' : 'Save'}
                          </button>
                          {notifSaved && <span style={{ fontSize: 11, color: '#16a34a' }}>Saved</span>}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
